import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { AuthGetterTypes } from '~/store/account/auth'
import {
  ActiveUserActions,
  ActiveUserGetterTypes,
  ActiveUserModuleActions,
  StarredRepoKey,
  StarredRepoNode
} from '~/store/user/active'

import { SupportTierChoices, TeamMemberRoleChoices, User } from '~/types/types'

export interface SubscribedPlanInfo {
  name: string
  slug: string
}

export enum ScaPlanType {
  Premium = 'ps',
  Free = 'pf'
}

export interface ScaPlanInfo {
  plan: ScaPlanType
  billing_interval: 'monthly' | 'annual' | null
  billed_targets_count: number
  target_quantity: number
}

export interface TeamPermissions {
  permission: TeamMemberRoleChoices
  canAddMember: boolean
  isPrimaryUser: boolean
  hasAllRepoAccess: boolean
}

export const ACTIVE_CONTEXT_COOKIE_NAME = 'current-active-organization'
const COOKIE_SEPERATOR = '::'

export interface DashboardContext {
  id: number
  login: string
  gql_node_id: string
  avatar_url: string
  vcs_provider: string
  vcs_provider_display: string
  vcs_installation_id: string
  type: string
  num_members_total: number
  is_default: boolean
  has_premium_plan: boolean
  has_gh_education_plan: false
  subscription_created: boolean
  is_primary_user_for_owner: boolean
  can_add_member: boolean
  has_granted_all_repo_access: boolean
  app_configuration_url: string
  role: TeamMemberRoleChoices
  team_name: string
  vcs_url: string
  subscribed_plan_info: SubscribedPlanInfo
  sca_plan_info: ScaPlanInfo
  support_tier: SupportTierChoices
  is_beta_tester: boolean
}

export interface ActiveUserStoreComputed {
  viewer: () => User
  viewerStarredRepos: () => Map<StarredRepoKey, StarredRepoNode[]>
}

export interface ActiveUserStoreActions {
  fetchActiveUser: (
    args?: Parameters<ActiveUserModuleActions[ActiveUserActions.FETCH_VIEWER_INFO]>[1]
  ) => ReturnType<ActiveUserModuleActions[ActiveUserActions.FETCH_VIEWER_INFO]>
  fetchActiveUserGitlabAccounts: () => ReturnType<
    ActiveUserModuleActions[ActiveUserActions.FETCH_GITLAB_ACCOUNTS]
  >
  fetchActiveUserGSRProjects: () => ReturnType<
    ActiveUserModuleActions[ActiveUserActions.FETCH_GSR_PROJECTS]
  >
  fetchActiveUserADSOrganizations: (
    args: Parameters<ActiveUserModuleActions[ActiveUserActions.FETCH_ADS_ORGANIZATIONS]>[1]
  ) => ReturnType<ActiveUserModuleActions[ActiveUserActions.FETCH_ADS_ORGANIZATIONS]>
  fetchActiveUserBBDProjects: () => ReturnType<
    ActiveUserModuleActions[ActiveUserActions.FETCH_BBD_PROJECTS]
  >
  fetchStarredRepos: (
    args: Parameters<ActiveUserModuleActions[ActiveUserActions.FETCH_STARRED_REPOS]>[1]
  ) => ReturnType<ActiveUserModuleActions[ActiveUserActions.FETCH_STARRED_REPOS]>
  fetchAccountInfo: (
    args: Parameters<ActiveUserModuleActions[ActiveUserActions.FETCH_ACCOUNT_INFO]>[1]
  ) => ReturnType<ActiveUserModuleActions[ActiveUserActions.FETCH_ACCOUNT_INFO]>
  fetchWorkspaces: (
    args: Parameters<ActiveUserModuleActions[ActiveUserActions.FETCH_WORKSPACES]>[1]
  ) => ReturnType<ActiveUserModuleActions[ActiveUserActions.FETCH_WORKSPACES]>
  updateUserDetails: (
    args: Parameters<ActiveUserModuleActions[ActiveUserActions.UPDATE_USER_DETAILS]>[1]
  ) => ReturnType<ActiveUserModuleActions[ActiveUserActions.UPDATE_USER_DETAILS]>
  deleteUser: (
    args: Parameters<ActiveUserModuleActions[ActiveUserActions.DELETE_USER]>[1]
  ) => ReturnType<ActiveUserModuleActions[ActiveUserActions.DELETE_USER]>
  fetchIsViewerSuperadmin: () => ReturnType<
    ActiveUserModuleActions[ActiveUserActions.FETCH_VIEWER_SUPERADMIN]
  >
  fetchRecommendedIssues: () => ReturnType<
    ActiveUserModuleActions[ActiveUserActions.FETCH_RECOMMENDED_ISSUES]
  >
  updateStarredRepo: (
    args: Parameters<ActiveUserModuleActions[ActiveUserActions.UPDATE_STARRED_REPO]>[1]
  ) => ReturnType<ActiveUserModuleActions[ActiveUserActions.UPDATE_STARRED_REPO]>
  updateDefaultContextAPI: (
    args: Parameters<ActiveUserModuleActions[ActiveUserActions.UPDATE_DEFAULT_CONTEXT]>[1]
  ) => ReturnType<ActiveUserModuleActions[ActiveUserActions.UPDATE_DEFAULT_CONTEXT]>
}

export interface ActiveUserStoreGetters {
  userHomeUrl: () => string
  isViewerSuperadmin: () => boolean
  homeUrl: () => string
}

export interface AuthStoreGetters {
  jwtExpiry: () => number
}

export default defineComponent({
  name: 'ActiveUserMixin',

  computed: {
    ...(mapState('user/active', {
      viewer: (state: any) => state.viewer,
      viewerStarredRepos: (state: any) => state.userStarredRepositories
    }) as ActiveUserStoreComputed),

    ...(mapGetters('user/active', {
      userHomeUrl: ActiveUserGetterTypes.GET_HOME_URL,
      isViewerSuperadmin: ActiveUserGetterTypes.IS_VIEWER_SUPERADMIN,
      homeUrl: ActiveUserGetterTypes.GET_HOME_URL
    }) as ActiveUserStoreGetters),

    ...(mapGetters('account/auth', {
      jwtExpiry: AuthGetterTypes.EXPIRY
    }) as AuthStoreGetters),

    teamPerms(): TeamPermissions {
      const context = this.activeDashboardContext as DashboardContext
      return {
        permission: context.role,
        canAddMember: context.can_add_member,
        isPrimaryUser: context.is_primary_user_for_owner,
        hasAllRepoAccess: context.has_granted_all_repo_access
      }
    },

    activeProvider(): string {
      if ('vcs_provider' in this.activeDashboardContext) {
        return (this.activeDashboardContext as DashboardContext).vcs_provider
      }
      return this.$route.params.provider as string
    },

    activeProviderName(): string {
      return this.$providerMetaMap[this.activeProvider].text
    },

    activeOwner(): string {
      if ('login' in this.activeDashboardContext) {
        return (this.activeDashboardContext as DashboardContext).login
      }
      return this.$route.params.owner as string
    },

    defaultDashbaordContext(): DashboardContext | null {
      if (this.viewer.dashboardContext) {
        const contextArray = this.viewer.dashboardContext.filter((context: DashboardContext) => {
          return context.is_default
        })

        if (contextArray.length > 0) {
          return contextArray[0]
        }
      }
      return null
    },

    primaryDashboardContext(): DashboardContext | null {
      if (this.viewer.dashboardContext) {
        const primaryAccounts = this.viewer.dashboardContext.filter((context: DashboardContext) => {
          return context.is_primary_user_for_owner
        })

        if (primaryAccounts.length > 0) {
          return primaryAccounts[0]
        }
      }
      return null
    },

    activeDashboardContext(): DashboardContext | Record<string, string> {
      let { provider, owner } = this.$route.params

      if (!provider && !owner && this.$cookies.get(ACTIVE_CONTEXT_COOKIE_NAME)) {
        const contextInCookie: Array<string> = this.$cookies
          .get(ACTIVE_CONTEXT_COOKIE_NAME)
          .split(COOKIE_SEPERATOR)

        if (Object.keys(this.$providerMetaMap).includes(contextInCookie[0])) {
          provider = contextInCookie[0]
          owner = contextInCookie[1]
        }
      }

      if (!this.isContextAllowed(provider as string, owner as string)) {
        if (this.defaultDashbaordContext) {
          return this.defaultDashbaordContext
        }

        if (this.primaryDashboardContext) {
          return this.primaryDashboardContext
        }
      }

      if (this.viewer.dashboardContext) {
        const activeContext = this.viewer.dashboardContext.filter((context: DashboardContext) => {
          return context.vcs_provider === provider && context.login === owner
        })

        if (activeContext.length >= 1) {
          return activeContext[0]
        }
      }

      return this.viewer?.dashboardContext?.[0] || {}
    }
  },

  async fetch(): Promise<void> {
    await this.fetchActiveUserIfLoggedIn()
  },

  methods: {
    ...(mapActions('user/active', {
      fetchActiveUser: ActiveUserActions.FETCH_VIEWER_INFO,
      fetchActiveUserGitlabAccounts: ActiveUserActions.FETCH_GITLAB_ACCOUNTS,
      fetchActiveUserGSRProjects: ActiveUserActions.FETCH_GSR_PROJECTS,
      fetchActiveUserADSOrganizations: ActiveUserActions.FETCH_ADS_ORGANIZATIONS,
      fetchActiveUserBBDProjects: ActiveUserActions.FETCH_BBD_PROJECTS,
      fetchStarredRepos: ActiveUserActions.FETCH_STARRED_REPOS,
      fetchAccountInfo: ActiveUserActions.FETCH_ACCOUNT_INFO,
      fetchWorkspaces: ActiveUserActions.FETCH_WORKSPACES,
      updateUserDetails: ActiveUserActions.UPDATE_USER_DETAILS,
      deleteUser: ActiveUserActions.DELETE_USER,
      fetchIsViewerSuperadmin: ActiveUserActions.FETCH_VIEWER_SUPERADMIN,
      fetchRecommendedIssues: ActiveUserActions.FETCH_RECOMMENDED_ISSUES,
      updateStarredRepo: ActiveUserActions.UPDATE_STARRED_REPO,
      updateDefaultContextAPI: ActiveUserActions.UPDATE_DEFAULT_CONTEXT
    }) as ActiveUserStoreActions),

    async fetchActiveUserIfLoggedIn(): Promise<void> {
      const now = (new Date().getTime() + 30_000) / 1000
      const expiry = this.jwtExpiry
      if (now < expiry) {
        await this.fetchActiveUser({})
      }
    },

    setActiveContextCookie(provider: string, owner: string): void {
      this.$cookies.remove(ACTIVE_CONTEXT_COOKIE_NAME)
      this.$cookies.set(ACTIVE_CONTEXT_COOKIE_NAME, [provider, owner].join(COOKIE_SEPERATOR))
    },

    logErrorForUser(
      e: Error,
      context: string,
      params: Record<string, unknown>,
      toastMessage?: `${string}.` | undefined
    ): void {
      this.$logErrorAndToast(e, toastMessage, this.viewer, { context, params })
    },

    async refetchUser(): Promise<void> {
      await this.fetchActiveUser({ refetch: true })
    },

    isContextAllowed(provider: string, owner: string): boolean {
      if (provider === undefined || owner === undefined) {
        return false
      }

      if (this.viewer.dashboardContext) {
        return (
          this.viewer.dashboardContext.filter((context: DashboardContext) => {
            return context.vcs_provider === provider && context.login === owner
          }).length > 0
        )
      }

      return false
    }
  }
})
